/**
 * Bridge for in-person Zoom recording.
 *
 * Appointment page stays put (session tools). Zoom runs in a script-opened *popup* so
 * End meeting can close it. Focus return is best-effort (browsers often keep the popup
 * frontmost); providers can click the appointment tab if needed.
 *
 * BroadcastChannel carries mute / devices / end between the windows.
 */

const IN_PERSON_RECORDING_WINDOW_NAME_PREFIX = 'unify-ipr-';
const IPR_HANDOFF_STORAGE_PREFIX = 'unify.ipr.handoff.';

/** Prefer a popup window over a full browser tab. */
export const IN_PERSON_RECORDING_HOST_POPUP_FEATURES =
  'popup=yes,width=520,height=480,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes';

export function inPersonRecordingWindowName(appointmentId: string): string {
  return `${IN_PERSON_RECORDING_WINDOW_NAME_PREFIX}${appointmentId}`;
}

export function inPersonRecordingChannelName(appointmentId: string): string {
  return `unify.in-person-recording.${appointmentId}`;
}

export function createInPersonRecordingSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ipr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

type IprHandoffPayload = {
  sessionId: string;
  meetingId: string;
  createdAt: number;
};

function handoffStorageKey(appointmentId: string): string {
  return `${IPR_HANDOFF_STORAGE_PREFIX}${appointmentId}`;
}

export function writeIprHandoff(
  appointmentId: string,
  payload: Pick<IprHandoffPayload, 'sessionId' | 'meetingId'>,
): void {
  try {
    const body: IprHandoffPayload = {
      sessionId: payload.sessionId,
      meetingId: payload.meetingId,
      createdAt: Date.now(),
    };
    sessionStorage.setItem(handoffStorageKey(appointmentId), JSON.stringify(body));
  } catch {
    // private mode / quota
  }
}

export function readIprHandoff(appointmentId: string): IprHandoffPayload | null {
  try {
    const raw = sessionStorage.getItem(handoffStorageKey(appointmentId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<IprHandoffPayload>;
    if (!parsed.sessionId?.trim()) return null;
    if (typeof parsed.createdAt === 'number' && Date.now() - parsed.createdAt > 2 * 60 * 60 * 1000) {
      sessionStorage.removeItem(handoffStorageKey(appointmentId));
      return null;
    }
    return {
      sessionId: parsed.sessionId.trim(),
      meetingId: parsed.meetingId?.trim() ?? '',
      createdAt: parsed.createdAt ?? Date.now(),
    };
  } catch {
    return null;
  }
}

export function clearIprHandoff(appointmentId: string): void {
  try {
    sessionStorage.removeItem(handoffStorageKey(appointmentId));
  } catch {
    // ignore
  }
}

type WithSession = { sessionId: string };

export type InPersonRecordingAudioDevice = {
  deviceId: string;
  label: string;
};

type InPersonRecordingHostToAppt =
  | ({ type: 'host-hello' } & WithSession)
  | ({ type: 'joined'; joinedAt: number } & WithSession)
  | ({ type: 'join-error'; message: string } & WithSession)
  | ({ type: 'mute-state'; muted: boolean } & WithSession)
  | ({
      type: 'audio-devices';
      devices: InPersonRecordingAudioDevice[];
      selectedDeviceId: string | null;
    } & WithSession)
  | ({ type: 'host-closing' } & WithSession)
  | ({ type: 'ended' } & WithSession);

type InPersonRecordingApptToHost =
  | ({ type: 'set-mute'; muted: boolean } & WithSession)
  | ({ type: 'set-audio-input'; deviceId: string } & WithSession)
  | ({ type: 'request-devices' } & WithSession)
  | ({ type: 'end' } & WithSession);

export type InPersonRecordingBridgeMessage =
  | InPersonRecordingHostToAppt
  | InPersonRecordingApptToHost;

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export type InPersonRecordingBridgeMessageBody = DistributiveOmit<
  InPersonRecordingBridgeMessage,
  'sessionId'
>;

export function openInPersonRecordingChannel(
  appointmentId: string,
): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(inPersonRecordingChannelName(appointmentId));
}

export function refocusOpenerWindow(): void {
  try {
    window.focus();
  } catch {
    // ignore
  }
}

/** From the host popup: best-effort return focus to the appointment opener. */
export function focusOpenerFromHostTab(): void {
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.focus();
      return;
    }
  } catch {
    // COOP / cross-origin
  }
  try {
    window.blur();
  } catch {
    // ignore
  }
}

function yieldHostFocusToAppointment(host: Window | null): void {
  try {
    host?.blur();
  } catch {
    // ignore
  }
  refocusOpenerWindow();
}

/**
 * Open a blank COI host popup during the user gesture (so navigate after await is allowed
 * and End can close the window).
 */
export function openBlankInPersonRecordingHostTab(appointmentId: string): Window | null {
  const tab = window.open(
    'about:blank',
    inPersonRecordingWindowName(appointmentId),
    IN_PERSON_RECORDING_HOST_POPUP_FEATURES,
  );
  if (tab) {
    try {
      tab.document.title = 'Unify recording';
      tab.document.body.innerHTML =
        '<p style="font-family:system-ui,sans-serif;padding:1.25rem;color:#444">' +
        'Starting recording…</p>';
    } catch {
      // ignore
    }
  }
  yieldHostFocusToAppointment(tab);
  return tab;
}

export function navigateInPersonRecordingHostTab(tab: Window, url: string): void {
  try {
    tab.location.href = url;
  } catch {
    tab.location.assign(url);
  }
  yieldHostFocusToAppointment(tab);
}

export function openInPersonRecordingHostTab(url: string, appointmentId: string): Window | null {
  const tab = window.open(
    url,
    inPersonRecordingWindowName(appointmentId),
    IN_PERSON_RECORDING_HOST_POPUP_FEATURES,
  );
  yieldHostFocusToAppointment(tab);
  return tab;
}

export function closeInPersonRecordingHostTab(appointmentId: string): void {
  try {
    const existing = window.open(
      '',
      inPersonRecordingWindowName(appointmentId),
      IN_PERSON_RECORDING_HOST_POPUP_FEATURES,
    );
    if (existing && !existing.closed) {
      existing.close();
    }
  } catch {
    // ignore
  }
}
